<template>
  <app-page :title="$t('profile.title')" icon="circle-user">
    <sme-alert v-if="isMfaGated" level="warning" class="my-3">
      <strong>{{ $t('profile.mfaGate.setupRequiredTitle') }}</strong
      >&nbsp;
      <template v-if="selfServicePhoneEnrolment">
        <template v-if="!state.user.phone">{{ $t('profile.mfaGate.addNumberPrompt') }}</template>
        <template v-else>{{ $t('profile.mfaGate.verifyNumberPrompt') }}</template>
        {{ $t('profile.mfaGate.completeSetupSuffix') }}
      </template>
      <template v-else>
        {{ $t('profile.mfaGate.contactSupportMessage') }}
      </template>
    </sme-alert>
    <sme-alert v-else-if="!state.user.requires_2fa && selfServicePhoneEnrolment" level="info" class="my-3">
      <template v-if="!state.user.phone">{{ $t('profile.mfaGate.addNumberPrompt') }}</template>
      <template v-else-if="!state.user.phone_verified">{{ $t('profile.mfaGate.verifyNumberPrompt') }}</template>
      <template v-else>{{ $t('profile.twoFactor.clickEnablePrompt') }}</template>
      {{ $t('profile.twoFactor.turnOnSuffix') }}
    </sme-alert>
    <page-sub-header :title="$t('profile.twoFactor.pageTitle')" title-tag="h2" class="mb-3" />
    <sme-alert v-if="state.user.requires_2fa" level="good" class="my-3">
      {{ $t('profile.twoFactor.enabledMessage') }}
    </sme-alert>

    <p v-if="state.user.phone">
      {{ $t('profile.twoFactor.phoneRegisteredLabel') }} <code>{{ state.user.phone }}</code
      >.<template v-if="!selfServicePhoneEnrolment"> {{ $t('profile.twoFactor.contactSupportToChange') }}</template>
    </p>
    <p v-else-if="!selfServicePhoneEnrolment" class="mb-0">
      {{ $t('profile.twoFactor.noPhoneConfigured') }}
    </p>

    <!-- Self-service phone enrolment (feature-flagged). When off, behaviour is unchanged (contact support). -->
    <template
      v-if="
        selfServicePhoneEnrolment && (!state.user.phone || !state.user.phone_verified || changingPhone) && !activating
      "
    >
      <p class="mb-2">
        {{
          state.user.phone
            ? $t('profile.phoneEnrolment.enterNewNumberPrompt')
            : $t('profile.phoneEnrolment.addNumberToEnablePrompt')
        }}
      </p>
      <ValidationObserver v-if="!codeSent" v-slot="v">
        <b-form @submit.prevent="v.handleSubmit(sendCode)">
          <app-input
            v-model="phoneInput"
            type="text"
            name="mobile number"
            :label="$t('profile.phoneEnrolment.mobileNumberLabel')"
            :placeholder="isUkCompany ? '+44 7700 900000' : '+1 555 123 4567'"
            :rules="{ required: true }"
            :disabled="sending"
            :validate-immediate="false"
          />
          <b-button type="submit" variant="primary" :disabled="v.invalid || sending">
            <b-spinner v-if="sending" class="mr-2" small />
            {{ sending ? $t('profile.phoneEnrolment.sendingButton') : $t('profile.phoneEnrolment.sendCodeButton') }}
          </b-button>
          <b-button v-if="changingPhone" variant="link" class="ml-2" :disabled="sending" @click="cancelChangePhone">
            {{ $t('profile.phoneEnrolment.cancelButton') }}
          </b-button>
        </b-form>
      </ValidationObserver>

      <ValidationObserver v-else :key="`verify-${phoneInput}`" v-slot="v">
        <b-form @submit.prevent="v.handleSubmit(verifyCode)">
          <i18n path="profile.phoneEnrolment.codeSentMessage" tag="p">
            <template #code>
              <b>{{ $t('profile.phoneEnrolment.sixDigitCodeLabel') }}</b>
            </template>
            <template #phone>
              <code>{{ phoneInput }}</code>
            </template>
          </i18n>
          <app-input
            v-model="verificationCode"
            type="text"
            name="verification code"
            :label="$t('profile.phoneEnrolment.verificationCodeLabel')"
            :placeholder="$t('profile.phoneEnrolment.sixDigitCodeLabel')"
            :rules="{ required: true, regex: /^\d{6}$/ }"
            :disabled="verifying"
            :validate-immediate="false"
          />
          <b-button type="submit" variant="primary" :disabled="v.invalid || verifying">
            <b-spinner v-if="verifying" class="mr-2" small />
            {{ verifying ? $t('profile.phoneEnrolment.verifyingButton') : $t('profile.phoneEnrolment.verifyButton') }}
          </b-button>
          <b-button variant="link" class="ml-2" :disabled="verifying" @click="resetToSendCode">
            {{ $t('profile.phoneEnrolment.useDifferentNumberButton') }}
          </b-button>
        </b-form>
      </ValidationObserver>
    </template>

    <b-button
      v-if="selfServicePhoneEnrolment && state.user.phone && !changingPhone && !activating"
      variant="link"
      class="px-0"
      @click="startChangePhone"
    >
      {{ $t('profile.phoneEnrolment.changePhoneNumberButton') }}
    </b-button>

    <template v-if="!state.user.requires_2fa && state.user.phone && !activating && !changingPhone">
      <b-button variant="primary" @click="activate"> {{ $t('profile.twoFactor.enableButton') }} </b-button>
    </template>

    <template v-if="activating">
      <app-loading :loading="!challenge" />
      <template v-if="challenge">
        <i18n v-if="state.user.phone" path="profile.activation.pinSentMessage" tag="p">
          <template #pin>
            <b>{{ $t('profile.activation.sixDigitPinLabel') }}</b>
          </template>
        </i18n>
        <ValidationObserver v-slot="v">
          <b-form @submit.prevent="v.handleSubmit(onSubmit)">
            <app-input
              v-model="challengePin"
              type="text"
              name="challenge pin"
              :placeholder="$t('profile.activation.sixDigitPinLabel')"
              :label="$t('profile.activation.pinLabel')"
              :description="$t('profile.activation.pinDescription')"
              :rules="{ required: true, regex: /^\d{6}$/ }"
              :disabled="challenging"
              :validate-immediate="false"
            />
            <page-sub-footer>
              <template #before>
                <sme-alert v-if="error" level="danger">{{ error }}</sme-alert>
              </template>
              <b-button type="submit" variant="primary" :disabled="v.invalid || challenging">
                <b-spinner v-if="challenging" class="mr-2" small />
                {{ challenging ? $t('common.submittingButton') : $t('common.submitButton') }}
              </b-button>
            </page-sub-footer>
          </b-form>
        </ValidationObserver>
      </template>
    </template>

    <sme-alert v-if="error && !activating" level="danger" class="mt-3">{{ error }}</sme-alert>
  </app-page>
</template>

<script>
import { ValidationObserver } from 'vee-validate';
import ApiClient from '@/ApiClient';
import AppInput from '@/components/AppInput.vue';
import AppLoading from '@/components/AppLoading.vue';
import AppPage from '@/components/AppPage.vue';
import PageSubFooter from '@/components/PageSubFooter.vue';
import PageSubHeader from '@/components/PageSubHeader.vue';
import SmeAlert from '@/components/atoms/SmeAlert.vue';
import useFeatureFlags from '@/composables/useFeatureFlags';
import State from '@/state/State';
import { isMfaComplete } from '@/utils/mfaGate';

export default {
  name: 'Profile',
  components: { ValidationObserver, AppInput, AppLoading, AppPage, PageSubFooter, PageSubHeader, SmeAlert },
  setup() {
    const { selfServicePhoneEnrolment, isUkCompany, enforceMfaSetup } = useFeatureFlags();
    return { selfServicePhoneEnrolment, isUkCompany, enforceMfaSetup };
  },
  computed: {
    isMfaGated() {
      return this.enforceMfaSetup && !isMfaComplete(this.state.user);
    },
  },
  data() {
    return {
      challenge: undefined,
      challengePin: undefined,
      error: undefined,
      activating: false,
      challenging: false,
      // self-service phone enrolment
      phoneInput: undefined,
      verificationCode: undefined,
      codeSent: false,
      changingPhone: false,
      sending: false,
      verifying: false,
      state: State.state,
    };
  },
  methods: {
    startChangePhone() {
      this.changingPhone = true;
      this.codeSent = false;
      this.phoneInput = undefined;
      this.verificationCode = undefined;
      this.error = undefined;
    },
    cancelChangePhone() {
      this.changingPhone = false;
      this.codeSent = false;
      this.phoneInput = undefined;
      this.verificationCode = undefined;
      this.error = undefined;
    },
    resetToSendCode() {
      this.codeSent = false;
      this.verificationCode = undefined;
      this.error = undefined;
    },
    async sendCode() {
      this.sending = true;
      this.error = undefined;
      try {
        await ApiClient.sendPhoneVerification(this.phoneInput, this.state.user.user_id);
        this.codeSent = true;
      } catch (error) {
        this.error =
          error.variables?.message || error.message || this.$t('profile.phoneEnrolment.sendCodeErrorFallback');
      } finally {
        this.sending = false;
      }
    },
    async verifyCode() {
      this.verifying = true;
      this.error = undefined;
      try {
        await ApiClient.verifyPhone(this.state.user.user_id, this.phoneInput, this.verificationCode);
        await ApiClient.getUser();
        this.codeSent = false;
        this.changingPhone = false;
        this.$appToast(this.$t('profile.phoneEnrolment.verifiedToastBody'), {
          title: this.$t('profile.twoFactor.pageTitle'),
          variant: 'success',
        });
        // Redirect to dashboard - backend auto-enables 2FA when enforce_mfa_setup is on
        if (this.enforceMfaSetup) {
          this.$router.push({ name: 'sme-dashboard' });
        }
      } catch (error) {
        this.error =
          error.variables?.message || error.message || this.$t('profile.phoneEnrolment.verifyCodeErrorFallback');
      } finally {
        this.verifying = false;
      }
    },
    activate() {
      this.activating = true;
      ApiClient.createChallenge2fa(this.state.user.user_id).then(
        challenge => {
          this.challenge = challenge;
        },
        error => {
          this.error = error;
        },
      );
    },
    onSubmit() {
      this.challenging = true;
      ApiClient.checkChallenge2fa(this.challenge.mfa_id, this.challengePin).then(
        response => {
          if (response.is_valid) {
            const newUser = {
              user_id: this.state.user.user_id,
              requires_2fa: true,
            };
            ApiClient.patchUser(newUser, this.state.company.company_id).then(
              () => {
                this.$appToast(this.$t('profile.activation.enabledToastBody'), {
                  title: this.$t('profile.twoFactor.enableButton'),
                  variant: 'success',
                });
                ApiClient.getUser().then(
                  () => {
                    this.activating = false;
                    this.challenging = false;
                  },
                  error => {
                    this.error = error.message;
                    this.challenging = false;
                  },
                );
              },
              error => {
                this.error = error.message;
                this.challenging = false;
              },
            );
          } else {
            this.error = this.$t('profile.activation.invalidPinError');
            this.challenging = false;
          }
        },
        error => {
          this.challenging = false;
          this.error = error.message;
        },
      );
    },
  },
};
</script>
